import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send } from "lucide-react";
import PublicLayout, { H1, Lead } from "../../components/PublicLayout";
import { SITE } from "../../content/site";
import { routePath, SITE_URL } from "../../lib/routes";
import { CONTACT_EMAIL } from "../../lib/media";
import { ORG_ID } from "../../components/Seo";
import { api } from "../../lib/api";
import { track, EVENTS } from "../../lib/analytics";

const inputCls = "w-full rounded-xl border border-black/10 bg-[#f6f5f2] px-4 py-3 text-sm font-light outline-none transition-colors focus:border-[#0e7c6b]";

export default function Contact({ lang }) {
  const c = SITE[lang].contact;
  const about = SITE[lang].about;
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const started = useRef(false);
  useEffect(() => { window.scrollTo(0, 0); }, [lang]);

  const onChange = (k) => (e) => {
    if (!started.current) { started.current = true; track(EVENTS.CONTACT_FORM_STARTED, {}); }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/public/contact", { ...form, language: lang, page: window.location.pathname });
      setStatus("success");
      track(EVENTS.CONTACT_FORM_SUBMITTED, {});
      setForm({ name: "", email: "", company: "", message: "", website: "" });
    } catch { setStatus("error"); }
  };

  const ld = [{
    "@context": "https://schema.org", "@type": "ContactPage",
    name: c.h1, url: `${SITE_URL}${routePath("contact", lang)}`, inLanguage: lang,
    mainEntity: { "@id": ORG_ID },
  }];

  return (
    <PublicLayout pageKey="contact" lang={lang} jsonLd={ld}
      crumbs={[{ name: SITE[lang].breadcrumbs.contact, path: routePath("contact", lang) }]}>
      <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-8 md:py-20" data-testid="contact-page">
        <H1>{c.h1}</H1>
        <Lead>{c.intro}</Lead>
        <div className="mt-14 grid gap-14 md:grid-cols-[1fr_320px]">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate={false}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="ct-name" className="mb-1.5 block text-xs font-medium text-[#6b6f6c]">{c.form.name}</label>
                <input id="ct-name" data-testid="contact-name" required value={form.name} onChange={onChange("name")} className={inputCls} autoComplete="name" />
              </div>
              <div>
                <label htmlFor="ct-email" className="mb-1.5 block text-xs font-medium text-[#6b6f6c]">{c.form.email}</label>
                <input id="ct-email" data-testid="contact-email" type="email" required value={form.email} onChange={onChange("email")} className={inputCls} autoComplete="email" />
              </div>
            </div>
            <div>
              <label htmlFor="ct-company" className="mb-1.5 block text-xs font-medium text-[#6b6f6c]">{c.form.company}</label>
              <input id="ct-company" data-testid="contact-company" value={form.company} onChange={onChange("company")} className={inputCls} autoComplete="organization" />
            </div>
            <div>
              <label htmlFor="ct-message" className="mb-1.5 block text-xs font-medium text-[#6b6f6c]">{c.form.message}</label>
              <textarea id="ct-message" data-testid="contact-message" required value={form.message} onChange={onChange("message")} className={`${inputCls} min-h-[160px] resize-y`} />
            </div>
            <input type="text" name="website" value={form.website} onChange={onChange("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <button type="submit" data-testid="contact-submit" disabled={status === "sending"}
              className="inline-flex items-center gap-2 rounded-full bg-[#050505] px-7 py-3 text-sm font-medium text-white transition-opacity hover:opacity-85 disabled:opacity-50">
              {status === "sending" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {status === "sending" ? c.form.sending : c.form.submit}
            </button>
            <div aria-live="polite">
              {status === "success" && <p data-testid="contact-success" className="text-sm font-medium text-[#0e7c6b]">{c.form.success}</p>}
              {status === "error" && <p data-testid="contact-error" className="text-sm font-medium text-[#b4443a]">{c.form.error}</p>}
            </div>
          </form>
          <aside className="space-y-10">
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a9d98]">{c.direct}</h2>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link"
                onClick={() => track(EVENTS.CTA_CLICK, { cta: "contact_email" })}
                className="mt-2 block break-all text-sm font-medium text-[#0e7c6b] hover:underline">{CONTACT_EMAIL}</a>
            </section>
            <section>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a9d98]">{c.addressTitle}</h2>
              <address className="mt-2 text-sm font-light not-italic leading-relaxed text-[#6b6f6c]">
                {about.legal.map((l, i) => <span key={i} className="block">{l}</span>)}
              </address>
            </section>
            <section className="rounded-2xl border border-black/8 p-6">
              <h2 className="text-base font-medium text-[#050505]">{c.bookTitle}</h2>
              <p className="mt-2 text-sm font-light leading-relaxed text-[#6b6f6c]">{c.bookText}</p>
              <Link to={routePath("book", lang)} data-testid="contact-book-link"
                onClick={() => track(EVENTS.CTA_CLICK, { cta: "contact_book" })}
                className="mt-4 inline-block rounded-full bg-[#0e7c6b] px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90">{c.bookCta}</Link>
            </section>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}
